import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Apiurl from "../../environment/Apiurl";

function AddSalaryDetails() {
  const navigate = useNavigate();

  const [staffList, setStaffList] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    staff_id: "",
    first_name: "",
    mobile_no: "",
    department: "",
    effective_month: "",
    basic_pay: "",
    hra: "",
    other_allowances: "",
    pf_deduction: "",
    professional_tax: "",
    other_deductions: "",
    status: "Active",
    notes: "",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${Apiurl}/staff`);
      setStaffList(response.data || []);
    } catch (error) {
      console.error("Error fetching staff list:", error.message);
    }
  };

  useEffect(() => {
    fetchStaffList();
  }, []);

  const handleStaffSelect = (e) => {
    const selectedId = e.target.value;
    const staff = staffList.find((s) => s.staff_id === selectedId);
    setFormData((prev) => ({
      ...prev,
      staff_id: selectedId,
      first_name: staff?.first_name || "",
      mobile_no: staff?.mobile_no || "",
      department: staff?.department || "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Live totals preview (mirrors the backend pre-save calculation)
  const num = (v) => parseFloat(v) || 0;
  const totalAllowances = num(formData.hra) + num(formData.other_allowances);
  const grossSalary = num(formData.basic_pay) + totalAllowances;
  const totalDeductions =
    num(formData.pf_deduction) +
    num(formData.professional_tax) +
    num(formData.other_deductions);
  const netSalary = grossSalary - totalDeductions;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.staff_id) {
      setError("Please select a staff member.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${Apiurl}/salary-details`, {
        ...formData,
        basic_pay: num(formData.basic_pay),
        hra: num(formData.hra),
        other_allowances: num(formData.other_allowances),
        pf_deduction: num(formData.pf_deduction),
        professional_tax: num(formData.professional_tax),
        other_deductions: num(formData.other_deductions),
      });
      navigate("/hr-management/salary-details");
    } catch (error) {
      console.error("Error saving salary details:", error.message);
      setError("Failed to save salary details. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="card p-4">
        <h4 className="fw-bold mb-4">Add Salary Details</h4>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Staff</label>
              <select
                className="form-select"
                name="staff_id"
                value={formData.staff_id}
                onChange={handleStaffSelect}
                required
              >
                <option value="">Select staff</option>
                {staffList.map((s) => (
                  <option key={s._id} value={s.staff_id}>
                    {s.staff_id} - {s.first_name} {s.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Department</label>
              <input
                type="text"
                className="form-control"
                value={formData.department}
                disabled
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Mobile No.</label>
              <input
                type="text"
                className="form-control"
                value={formData.mobile_no}
                disabled
              />
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">Effective Month</label>
              <input
                type="month"
                className="form-control"
                name="effective_month"
                value={formData.effective_month}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <hr />
          <h6 className="fw-bold">Earnings</h6>
          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label">Basic Pay</label>
              <input
                type="number"
                className="form-control"
                name="basic_pay"
                value={formData.basic_pay}
                onChange={handleChange}
                required
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">HRA</label>
              <input
                type="number"
                className="form-control"
                name="hra"
                value={formData.hra}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Other Allowances</label>
              <input
                type="number"
                className="form-control"
                name="other_allowances"
                value={formData.other_allowances}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Gross Salary</label>
              <input
                type="number"
                className="form-control"
                value={grossSalary}
                disabled
              />
            </div>
          </div>

          <hr />
          <h6 className="fw-bold">Deductions</h6>
          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label">PF Deduction</label>
              <input
                type="number"
                className="form-control"
                name="pf_deduction"
                value={formData.pf_deduction}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Professional Tax</label>
              <input
                type="number"
                className="form-control"
                name="professional_tax"
                value={formData.professional_tax}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Other Deductions</label>
              <input
                type="number"
                className="form-control"
                name="other_deductions"
                value={formData.other_deductions}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Total Deductions</label>
              <input
                type="number"
                className="form-control"
                value={totalDeductions}
                disabled
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">Net Salary</label>
              <input
                type="number"
                className="form-control fw-bold"
                value={netSalary}
                disabled
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              name="notes"
              rows="2"
              value={formData.notes}
              onChange={handleChange}
            />
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate("/hr-management/salary-details")}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Salary Details"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddSalaryDetails;
